import { memo, useCallback, useMemo, useRef, useState } from 'react';
import type { IDashboardColumn, IDashboardProject } from 'models';
import { buildRowCells, type LookupMaps } from '../dashboardUtils';

/**
 * One project row (§3.E "Project rows"; project.tmpl). Renders:
 *  - the frozen name cell (`li.master-list-row.fixed-column.h-40.z-index-0`
 *    with the app `tooltip` attribute = full name; a pin svg when pinned; the
 *    readonly project-name-input), and
 *  - the dynamic cells (`li[name=<bareKey>] > span`) in column order, FK names
 *    resolved client-side.
 *  - the `.table-buttons` action cluster + the `.hover` overlay.
 *
 * Row action menu (parity 60-60g): the menu's visibility is CSS-`:focus`-driven
 * (`.table-buttons > button:nth-child(1):focus + .dropdown-edit`). Hovering the
 * `.icon-more-actions` button focuses it → menu opens; leaving the ROW blurs it
 * → menu closes; clicking the button toggles. `.z-100` raises the row while
 * open (300ms debounced reset). `.top` flips the menu upward near the table
 * bottom.
 *
 * Actions: Edit → open; Rename → inline unlock + focus, commit ≥3 chars else
 * revert; Duplicate → POST /project/duplicate + preloader; Pin/Unpin →
 * setCustomOrder; Delete → opens DeleteProjectConfirm.
 *
 * Gating (parity 131-142; CSS-only in Backbone, ENFORCED here): non-editable →
 * `.non-editable` (dims Rename + Delete); restricted permission →
 * `.ctrl-btns.non-clickable` (dims whole cluster); critical-task → Delete
 * `non-clickable`. Handlers ALSO bail on the gated conditions so the action
 * cannot fire even if the pointer-events guard is bypassed.
 */
interface ProjectRowProps {
  project: IDashboardProject;
  columns: IDashboardColumn[]; // ordered, visible (dynamic) columns
  maps: LookupMaps;
  pinned: boolean;
  selected: boolean;
  filtered: boolean; // a filter is active → no drag
  restrictedPermission: boolean; // permission !== 'access'
  unread: boolean; // unread new_project notification → .unread-notification
  onSelect: (id: number) => void;
  onOpen: (id: number) => void;
  onRename: (id: number, name: string) => void;
  onDuplicate: (project: IDashboardProject) => void;
  onPin: (project: IDashboardProject, pinned: boolean) => void;
  onRequestDelete: (id: number) => void;
  /** Drag handlers for the default (un-pinned, un-filtered) list. */
  dragProps?: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
  };
  dragging?: boolean;
}

/** 300ms debounce for the open-state reset (project.js event_blur). */
const BLUR_RESET_MS = 300;

/**
 * Menu geometry, mirroring whiteboard/styles.css so the flip can be decided
 * before the menu is painted: it is a fixed 190px box (:1514) offset 35px below
 * the action cluster (:582), or 195px above it when `.top` is set (:1541).
 */
const MENU_HEIGHT = 190;
const MENU_DROP_TOP = 35;
const MENU_FLIP_TOP = -195;

/**
 * Raise the row list holding the open menu above the other one, and lower the
 * rest (`z-100`, whiteboard/styles.css:1555 — it is what lifts the pinned list
 * over the default list, which is a later sibling and would otherwise paint on
 * top of it).
 *
 * project.js added the class on open and removed it 300ms after that row's own
 * blur. The class lives on the UL shared by every row in the list, so the timer
 * belonging to the row you just left fires last and strips it from under the row
 * you are now on — the open menu drops behind the rows below and looks
 * truncated. The same applies across lists: a default row's menu that flips up
 * into the pinned band is painted over while the pinned list is still raised.
 *
 * Derive the class from what actually holds focus instead — `:focus` is what
 * drives menu visibility, so it is the same source of truth the CSS uses.
 */
const syncRaisedList = () => {
  const focused = document.querySelector(
    '.admin-table.first-fixed-list ul.list-rows .table-buttons > button.icon-more-actions:focus'
  );
  const owner = focused?.closest('ul.list-rows') ?? null;
  document
    .querySelectorAll('.admin-table.first-fixed-list ul.list-rows')
    .forEach((ul) => ul.classList.toggle('z-100', ul === owner));
};

const PIN_PATH =
  'm640-480 80 80v80H520v240l-40 40-40-40v-240H240v-80l80-80v-280h-40v-80h400v80h-40v280Zm-286 80h252l-46-46v-314H400v314l-46 46Zm126 0Z';

const ProjectRow = ({
  project,
  columns,
  maps,
  pinned,
  selected,
  filtered,
  restrictedPermission,
  unread,
  onSelect,
  onOpen,
  onRename,
  onDuplicate,
  onPin,
  onRequestDelete,
  dragProps,
  dragging,
}: ProjectRowProps) => {
  const id = project.id;
  const name = project.name ?? '';
  const isCritical = !!project.is_critical_task_started;
  const editable = project.editable !== false;

  // Gating booleans (parity 131-142).
  const deleteDisabled = restrictedPermission || isCritical || !editable;
  const renameDisabled = restrictedPermission || !editable;
  const clusterDisabled = restrictedPermission; // whole cluster

  const cells = useMemo(
    () => buildRowCells(project, columns, maps),
    [project, columns, maps]
  );

  // ---- Refs ----
  const liRef = useRef<HTMLLIElement | null>(null);
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const fixedCellRef = useRef<HTMLLIElement | null>(null);

  // Open-state flag (project.js `this.clicked`) + blur-reset timer.
  const clickedRef = useRef(false);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Inline rename state ----
  // `renaming` controls the readonly attr + focus; `draft` holds the in-progress
  // value; commit on blur/Enter (≥3 chars else revert); Esc cancels.
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(name);
  // When the project name changes from the server and we are not renaming, sync
  // the draft so the input reflects the latest value.
  if (!renaming && draft !== name) setDraft(name);

  // ---- li.project-li classes (project.js render()) ----
  const liClass = [
    'project-li',
    !pinned && !filtered ? 'drag' : '',
    pinned ? 'project-pinned-li' : 'project-unpinned-li',
    !editable ? 'non-editable' : '',
    selected ? 'selected-li' : '',
    isCritical ? 'is_critical_task_started_li' : '',
    unread ? 'unread-notification' : '',
    dragging ? 'dragging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Clicking an inner control (button/input/select) must not select the row.
  const isInnerControl = (target: EventTarget | null) => {
    const el = target as HTMLElement | null;
    if (!el) return false;
    return !!el.closest('button, input, select');
  };

  // ---- Menu open/close (focus-driven; mirrors project.js event_focus/hover/
  //      leave/blur) ----
  const openMenu = useCallback(() => {
    if (clickedRef.current) return;
    const btn = moreBtnRef.current;
    if (!btn) return;
    clickedRef.current = true;
    // Compute up/down: flip upward only when the menu does not fit below.
    //
    // project.js event_focus did this with
    //   pos = table.height() - btn.getBoundingClientRect().top - table.offset().top
    // which subtracts two absolute page offsets from a height, so it flips on
    // the button's position in the window rather than on available room. The
    // table is `overflow: auto`, so a menu flipped up with nowhere to go is
    // clipped by its top edge and loses Edit/Rename — reproducible below ~740px
    // of viewport height. Measure the room instead.
    const table = document.querySelector('.admin-table.first-fixed-list') as HTMLElement | null;
    const dropdown = dropdownRef.current;
    if (table && dropdown) {
      const tableRect = table.getBoundingClientRect();
      const btnTop = btn.getBoundingClientRect().top;
      const menuHeight = dropdown.offsetHeight || MENU_HEIGHT;
      // The menu's top edge, per whiteboard/styles.css: `top: 35px` below the
      // cluster normally (:582), `top: -195px` above it with `.top` (:1541).
      // Both are relative to `.table-buttons`, whose top edge is the button's.
      const fitsBelow = btnTop + MENU_DROP_TOP + menuHeight <= tableRect.bottom;
      const fitsAbove = btnTop + MENU_FLIP_TOP >= tableRect.top;
      // Prefer down, as legacy does; flip up when down does not fit.
      const flip = !fitsBelow && fitsAbove;
      dropdown.classList.toggle('top', flip);
      if (fitsBelow || fitsAbove) {
        dropdown.style.removeProperty('top');
      } else {
        // Neither preset position fits — a table so short that no row can hold
        // the menu either side of its cluster. Clamp it into the table instead
        // of letting the overflow eat an option. Both stylesheet rules declare
        // `top` as !important, so the override has to as well.
        const clampedTop = Math.max(tableRect.top, tableRect.bottom - menuHeight);
        dropdown.style.setProperty('top', `${clampedTop - btnTop}px`, 'important');
      }
    }
    btn.focus();
    syncRaisedList();
  }, []);

  const closeMenu = useCallback(() => {
    moreBtnRef.current?.blur();
  }, []);

  // click .table-buttons button → event_focus (toggle open/close). Mirrors
  // project.js: the open path runs only when closed; when already open it blurs
  // the CLICKED button (e.currentTarget). For the more-actions button that hides
  // the menu (its :focus drives visibility); for a dropdown action button it is
  // a no-op on visibility (the menu stays open so the action click registers).
  // Only react to clicks ON a <button> inside the cluster.
  const onClusterClick = useCallback(
    (e: React.MouseEvent) => {
      const btn = (e.target as HTMLElement).closest('button');
      if (!btn) return;
      if (!clickedRef.current) openMenu();
      else btn.blur();
    },
    [openMenu]
  );

  // mouseover .icon-more-actions → event_hover (open if not already open).
  const onMoreHover = useCallback(() => {
    if (!clickedRef.current) openMenu();
  }, [openMenu]);

  // mouseleave (whole row) → event_leave (close if open).
  const onRowLeave = useCallback(() => {
    if (clickedRef.current) closeMenu();
  }, [closeMenu]);

  // blur .table-buttons button → event_blur (300ms debounced state reset).
  const onClusterBlur = useCallback(() => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => {
      clickedRef.current = false;
      syncRaisedList();
      blurTimerRef.current = null;
    }, BLUR_RESET_MS);
  }, []);

  // ---- Action handlers ----
  const handleEdit = useCallback(() => {
    if (clusterDisabled) return;
    onOpen(id);
  }, [clusterDisabled, onOpen, id]);

  const startRename = useCallback(() => {
    if (renameDisabled) return;
    // project.js rename_project: remove z-index-0 from the fixed cell, unlock
    // the input, then focus it after a tick.
    fixedCellRef.current?.classList.remove('z-index-0');
    setRenaming(true);
    setDraft(name);
    setTimeout(() => {
      const inp = nameInputRef.current;
      if (inp) {
        inp.focus();
        inp.select();
      }
    }, 0);
  }, [renameDisabled, name]);

  const commitRename = useCallback(() => {
    if (!renaming) return;
    setRenaming(false);
    // blurInput: re-add z-index-0.
    fixedCellRef.current?.classList.add('z-index-0');
    const trimmed = draft.trim();
    // changeProjectName: <3 chars → revert; else PATCH.
    if (trimmed.length < 3) {
      setDraft(name);
      return;
    }
    if (trimmed !== name) onRename(id, trimmed);
  }, [renaming, draft, name, onRename, id]);

  const cancelRename = useCallback(() => {
    setRenaming(false);
    fixedCellRef.current?.classList.add('z-index-0');
    setDraft(name);
  }, [name]);

  const handleDuplicate = useCallback(() => {
    if (clusterDisabled) return;
    onDuplicate(project);
  }, [clusterDisabled, onDuplicate, project]);

  const handlePin = useCallback(() => {
    if (clusterDisabled) return;
    onPin(project, pinned);
  }, [clusterDisabled, onPin, project, pinned]);

  const handleDelete = useCallback(() => {
    if (deleteDisabled) return;
    onRequestDelete(id);
  }, [deleteDisabled, onRequestDelete, id]);

  return (
    <li
      ref={liRef}
      className={liClass}
      data-project-id={id}
      data-id={id}
      onMouseLeave={onRowLeave}
      onClick={(e) => {
        if (isInnerControl(e.target)) return;
        onSelect(id);
      }}
      onDoubleClick={(e) => {
        if (isInnerControl(e.target)) return;
        onOpen(id);
      }}
      {...dragProps}
    >
      <ul className="project">
        <li
          ref={fixedCellRef}
          className="master-list-row fixed-column h-40 z-index-0"
          tooltip={name}
        >
          <ul>
            <li
              name="name"
              className={isCritical ? 'is_critical_task_started_li' : undefined}
            >
              {pinned && (
                <span className="pin-svg">
                  <svg height="16" viewBox="0 -960 960 960" width="16">
                    <path d={PIN_PATH} fill="#757575" />
                  </svg>
                </span>
              )}
              <a value={String(id)}>
                <input
                  ref={nameInputRef}
                  id={`project-name-input-${id}`}
                  value={renaming ? draft : name}
                  readOnly={!renaming}
                  className={`project-name-input${renaming ? ' not-readonly' : ''}`}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelRename();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  onDoubleClick={(e) => {
                    // Avoid opening the project when interacting with the name
                    // input directly.
                    e.stopPropagation();
                  }}
                />
              </a>
            </li>
          </ul>
        </li>
        <li>
          <ul>
            {cells.map((cell, i) => (
              <li key={`${cell.name}-${i}`} name={cell.name}>
                <span>{cell.value}</span>
              </li>
            ))}
          </ul>
        </li>
      </ul>

      {/* Row action cluster (parity 60-67, gating 131-142). */}
      <div
        className={`table-buttons ctrl-btns whiteboard-table-buttons${
          clusterDisabled ? ' non-clickable' : ''
        }`}
        onClick={onClusterClick}
        onBlur={onClusterBlur}
      >
        <button
          ref={moreBtnRef}
          className="icon-more-actions"
          type="button"
          onMouseOver={onMoreHover}
        />
        <div ref={dropdownRef} className="dropdown-edit">
          <button
            tooltip="Edit"
            name="edit"
            className="ka-tooltip-show-after icon-edit"
            type="button"
            onClick={handleEdit}
          >
            Edit
          </button>
          <button
            tooltip="Rename"
            name="rename"
            className="ka-tooltip-show-after"
            type="button"
            onClick={startRename}
          >
            <svg className="rename-icon" viewBox="0 0 24 24" width="24" height="20">
              <path
                fill="#9e9e9e"
                d="M15 16L11 20H21V16H15M12.06 7.19L3 16.25V20H6.75L15.81 10.94L12.06 7.19M18.71 8.04C19.1 7.65 19.1 7 18.71 6.63L16.37 4.29C16.17 4.09 15.92 4 15.66 4C15.41 4 15.15 4.1 14.96 4.29L13.13 6.12L16.88 9.87L18.71 8.04Z"
              />
            </svg>
            <span className="rename-text">Rename</span>
          </button>
          <button
            tooltip="Duplicate"
            name="duplicate"
            className="ka-tooltip-show-after icon-copy"
            type="button"
            onClick={handleDuplicate}
          >
            Duplicate
          </button>
          <button
            tooltip="Delete"
            name="delete"
            className={`${isCritical ? 'non-clickable ' : ''}ka-tooltip-show-after icon-trash`}
            type="button"
            onClick={handleDelete}
          >
            Delete
          </button>
          <button
            tooltip="Pin"
            name="pin"
            className="ka-tooltip-show-after"
            type="button"
            onClick={handlePin}
          >
            <svg height="16" viewBox="0 -960 960 960" width="16">
              <path d={PIN_PATH} fill="#757575" />
            </svg>
            {pinned ? 'Unpin ' : ' Pin'}
          </button>
        </div>
      </div>
      <div className="hover" />
    </li>
  );
};

export default memo(ProjectRow);
